/**
 * 路由分包加载失败时别白屏 —— 自愈一次，还不行就说人话。
 *
 * 路由是按页切分的（`MergeCreateView-<hash>.js`）。`import()` 被拒时
 * `vue-router` 的默认行为是**中止这次导航**：地址栏变了、内容区什么都不渲染，
 * 页面上一个字都没有，控制台之外没有任何线索。
 *
 * 用户 2026-08-14 在 Windows Edge 上撞到：量化导出与 LoRA 合并两页白屏，
 * 同一台机器的 Chrome 正常、Edge 无痕正常、**强制清空缓存刷新也不好**。
 * 后两条把 HTTP 缓存排除掉了（无痕没有它、硬刷新绕过它），
 * 而本仓从来没注册过 Service Worker（代码与 git 历史都零命中）。
 *
 * 根因还没定死，但**白屏本身就是缺陷**：加载不到一个分包时，
 * 正确的表现是重试一次（这能盖掉「产物换版而浏览器还拿着旧模块图」那一类），
 * 重试还不行就把错误摆出来让人能截图。
 */

/**
 * 这个错误是不是「分包拉不下来」。
 *
 * 各家浏览器的文案不一样，所以按**关键词集合**判而不是精确匹配：
 * Chrome/Edge 是 `Failed to fetch dynamically imported module`，
 * Firefox 是 `error loading dynamically imported module`，
 * Safari 是 `Importing a module script failed`，
 * Vite 自己的预载失败是 `Unable to preload CSS`。
 *
 * 判宽一点是有意的：误判的代价是多刷新一次，漏判的代价是一片白。
 */
export function isChunkLoadError(err: unknown): boolean {
  const text = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
  return (
    /dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /Unable to preload/i.test(text) ||
    /ChunkLoadError/i.test(text) ||
    /Loading chunk \S+ failed/i.test(text)
  )
}

/**
 * 该不该自动重载。
 *
 * @param err 路由抛出来的错误。
 * @param alreadyRetried 这一个会话里是不是已经为此重载过。
 * @returns `true` = 重载一次；`false` = 别再刷了，把错误摆出来。
 */
export function shouldReload(err: unknown, alreadyRetried: boolean): boolean {
  return isChunkLoadError(err) && !alreadyRetried
}

/** 面向用户的那句话。不出现堆栈、不出现文件名 —— 那些进控制台。 */
export const CHUNK_FAILED_TEXT =
  '这一页的资源没加载出来。已经自动重试过一次仍然失败，多半是浏览器扩展或安全策略拦了请求。换一个浏览器或用无痕窗口先继续，并把控制台里红色那一行发给运维。'

export interface RecoveryHost {
  reload: () => void
  getFlag: () => string | null
  setFlag: (value: string) => void
  clearFlag: () => void
  onGiveUp: (message: string) => void
}

/**
 * 把自愈逻辑接到一个路由上。
 *
 * @param router 只用它的 `onError` 与 `isReady`，所以类型放到最小 —— 单测不必造整个路由。
 * @param host 与浏览器打交道的那几件事，测试里可替换。
 */
export function installChunkRecovery(
  router: { onError: (fn: (err: unknown) => void) => void; isReady: () => Promise<unknown> },
  host: RecoveryHost,
): void {
  router.onError((err) => {
    if (!isChunkLoadError(err)) return
    if (shouldReload(err, host.getFlag() !== null)) {
      host.setFlag(String(Date.now()))
      host.reload()
      return
    }
    host.onGiveUp(CHUNK_FAILED_TEXT)
  })
  // 导航成功之后把钥匙还回去，否则一次偶发失败会让**以后**的分包错误都不再重试。
  void router.isReady().then(() => host.clearFlag())
}
